import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subscription, combineLatest, timer } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { UserFacade } from '../../../../../core/user';
import { EmailValidator } from '../../../../../shared/validators/email-validator';
import { LoginFormView } from '../../model/login-form-view.model';

@Component({
  selector: 'py-forgot-username',
  templateUrl: './forgot-username.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ForgotUsernameComponent implements OnInit {
  form: UntypedFormGroup;
  loginFormView = LoginFormView;
  error$: Observable<boolean>;
  success$: Observable<boolean>;
  loading$: Observable<boolean>;

  private subscription = new Subscription();

  constructor(
    private userService: UserFacade,
    private fb: UntypedFormBuilder
  ) {}

  @Output()
  changeView = new EventEmitter<LoginFormView>();

  ngOnInit(): void {
    this.createForm();
    this.initObservables();
  }

  requestReset(): void {
    this.userService.requestForgotUsernameEmail(this.form.get('email').value);
    this.subscription.add(
      timer(3000)
        .pipe(take(1))
        .subscribe(() => this.userService.resetRequestForgotUsernameEmailProcessingState())
    );
  }

  onResetInput(fieldName: string): void {
    this.form.get(fieldName).reset();
    this.form.get(fieldName).markAsDirty();
  }

  private initObservables(): void {
    this.error$ = this.userService
      .getRequestForgotUsernameEmailError()
      .pipe(map((forgotUsernameError) => Boolean(forgotUsernameError)));

    this.success$ = this.userService.getRequestForgotUsernameEmailSuccess().pipe(
      map((forgotUsernameSuccess) => Boolean(forgotUsernameSuccess)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.loading$ = this.userService.getRequestForgotUsernameEmailLoading();

    this.subscription.add(
      combineLatest([this.success$, this.error$])
        .pipe(filter(([success, error]) => Boolean(success) || Boolean(error)))
        .subscribe((_success) => {
          this.changeView.emit(this.loginFormView.EmailSentForgotUsername);
        })
    );
  }

  private createForm(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, EmailValidator.singleEmail]],
    });
  }
}
